import React, { useRef, useEffect, useCallback } from 'react';
import ConversationItem from './ConversationItem';
import './ConversationsList.css';

/**
 * ConversationsList Component
 * 
 * Displays paginated list of conversations with infinite scroll.
 * Automatically loads more conversations when user scrolls near bottom.
 * 
 * Features:
 * - Infinite scroll pagination
 * - Loading indicator
 * - Empty state
 * - Error handling
 * - Scroll-to-top on mount
 * 
 * @param {Array} conversations - List of conversation objects
 * @param {string} activeConversationId - Currently selected conversation ID
 * @param {Function} onSelectConversation - Callback when conversation is selected
 * @param {Function} onLoadMore - Callback to load more conversations
 * @param {boolean} isLoading - Loading state
 * @param {boolean} hasMore - Are there more conversations to load?
 * @param {Object} error - Error object (if any)
 */
const ConversationsList = ({
  conversations = [],
  activeConversationId = null,
  onSelectConversation = () => {},
  onLoadMore = () => {},
  isLoading = false,
  hasMore = false,
  error = null
}) => {
  const containerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const lastScrollTop = useRef(0);

  /**
   * Intersection Observer: Load more when user scrolls near bottom
   * 
   * This is more efficient than scroll event listener
   * Triggers when load-more sentinel element becomes visible
   */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          // When "load more" element comes into view and we have more to load
          if (entry.isIntersecting && hasMore && !isLoading) {
            console.log('[ConversationsList] Infinite scroll triggered');
            onLoadMore();
          }
        });
      },
      {
        root: containerRef.current,
        rootMargin: '100px', // Load when within 100px of bottom
        threshold: 0.1
      }
    );

    const node = loadMoreRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [hasMore, isLoading, onLoadMore]);

  /**
   * Smooth scroll to top when container mounts
   */
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, []);

  /**
   * Handle scroll position (for future features like scroll restoration)
   */
  const handleScroll = useCallback((e) => {
    lastScrollTop.current = e.target.scrollTop;
  }, []);

  // ============================================================
  // RENDER STATES
  // ============================================================

  // Empty state
  if (!isLoading && conversations.length === 0) {
    return (
      <div className="conversations-list conversations-empty">
        <div className="empty-icon">💬</div>
        <h3 className="empty-title">No conversations yet</h3>
        <p className="empty-message">
          Start a new chat to begin a conversation
        </p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="conversations-list conversations-error">
        <div className="error-icon">⚠️</div>
        <h3 className="error-title">Failed to load conversations</h3>
        <p className="error-message">{error.message}</p>
      </div>
    );
  }

  return (
    <div
      className="conversations-list"
      ref={containerRef}
      onScroll={handleScroll}
      role="region"
      aria-label="Past conversations"
    >
      {/* Conversations Grid */}
      {conversations.map((conversation) => (
        <ConversationItem
          key={conversation.conversation_id}
          conversation={conversation}
          isActive={conversation.conversation_id === activeConversationId}
          onSelect={onSelectConversation}
        />
      ))}

      {/* Loading indicator */}
      {isLoading && (
        <div className="conversations-loading" role="status" aria-live="polite">
          <div className="spinner"></div>
          <span>Loading conversations...</span>
        </div>
      )}

      {/* Infinite scroll trigger */}
      {hasMore && !isLoading && (
        <div
          ref={loadMoreRef}
          className="load-more-sentinel"
          aria-hidden="true"
        />
      )}

      {/* End of list message */}
      {!hasMore && conversations.length > 0 && (
        <div className="conversations-end" role="status">
          <p>No more conversations</p>
        </div>
      )}
    </div>
  );
};

export default ConversationsList;
